import React, { Suspense, lazy } from 'react';
import Confetti from 'react-confetti-boom';
import GameContent from './GameContent';
import GameHeaderControls from './GameHeaderControls';
import ScoreDisplay from './ScoreDisplay';
import LoadingScreen from './LoadingScreen';
import { filterText } from '../utils/challengeUtils';
import { MIN_YEAR, MAX_YEAR, COLORS } from '../constants';

const LazyOverallStats = lazy(() => import('./OverallStats'));

function ActiveChallenge({ state, handlers }: { state: any; handlers: any }) {
  const {
    currentRound,
    totalRounds,
    correctGuesses,
    score,
    pointsGained,
    isTodayChallenge,
    showAllTimeStats,
    showConfettiBoom,
    challenge,
    image,
    imageLoading,
    game,
    revealedDigits,
    revealUsedThisRound,
    allDigitsRevealed,
  } = state;

  const {
    onShowInstructions,
    setShowAllTimeStats,
    onShowCalendar,
    handleDigitInput,
    handleKeyDown,
    handlePaste,
    handleTimelineClick,
    handleSliderChange,
    handleGuess,
    handleHint,
    handleDigitReveal,
    handleNextRound,
  } = handlers;

  const censoredDescription = image.description
    ? filterText(image.description)
    : '';

  const censoredFilename = image.filename ? filterText(image.filename) : '';

  return (
    <div className="min-h-screen">
      <div className="flex justify-center items-center md:py-6">
        <div
          className="w-full max-w-[668px] md:border md:rounded md:mx-4"
          style={{
            backgroundColor: COLORS.PRIMARY_BACKGROUND,
            borderColor: COLORS.SECONDARY_BACKGROUND,
          }}
        >
          <div className="px-4 md:px-6 pb-4 relative flex justify-between items-center pt-4 h-[72px]">
            <ScoreDisplay
              round={currentRound + 1}
              totalRounds={totalRounds}
              correctGuesses={correctGuesses}
              score={score}
              pointsGained={pointsGained}
              onShowInstructions={onShowInstructions}
              gameMode="daily"
              isPastChallenge={!isTodayChallenge}
            />

            <GameHeaderControls
              onShowStats={() => setShowAllTimeStats(true)}
              onShowCalendar={() => {
                onShowCalendar();
                if (typeof window.gtag === 'function') {
                  window.gtag('event', 'open_calendar', {
                    current_view: 'daily',
                  });
                }
              }}
              onShowInstructions={onShowInstructions}
              gameMode="daily"
              hasDailyChallenge={!!challenge}
            />
          </div>

          <GameContent
            image={image}
            imageLoading={imageLoading}
            round={currentRound + 1}
            totalRounds={totalRounds}
            censoredDescription={censoredDescription}
            censoredFilename={censoredFilename}
            yearInput={game.yearInput}
            hasGuessed={game.hasGuessed}
            guessHistory={game.guessHistory}
            revealedDigits={game.revealedDigits}
            challengeRevealedDigits={revealedDigits}
            validationError={game.validationError}
            hintMessage={game.hintMessage}
            hintRequested={game.hintRequested}
            selectingDigit={game.selectingDigit}
            allDigitsRevealed={revealUsedThisRound || allDigitsRevealed}
            pointsGained={pointsGained}
            onDigitInput={handleDigitInput}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            onTimelineClick={handleTimelineClick}
            onSliderChange={handleSliderChange}
            onGuess={handleGuess}
            onHint={handleHint}
            onDigitReveal={handleDigitReveal}
            onNext={handleNextRound}
            isLastRound={state.isLastRound}
            minYear={MIN_YEAR}
            maxYear={MAX_YEAR}
          />
        </div>

        {showConfettiBoom && (
          <div
            style={{
              position: 'fixed',
              top: '150px',
              left: 0,
              width: '100vw',
              height: 'calc(100dvh - 150px)',
              overflow: 'hidden',
              zIndex: 1000,
              pointerEvents: 'none',
            }}
          >
            <Confetti
              mode="boom"
              particleCount={100}
              colors={[
                '#FFC300',
                '#7236DC',
                '#C6A4FF',
                '#FFFFFF',
                '#FF5F8A',
                '#50E3C2',
                '#FF9E5F',
                '#5B36D6',
              ]}
              x={0.5}
              y={0.5}
              spreadDeg={80}
              effectCount={2}
              effectInterval={4000}
              shapeSize={15}
              launchSpeed={0.8}
            />
          </div>
        )}
      </div>

      <Suspense fallback={<LoadingScreen />}>
        {showAllTimeStats && (
          <LazyOverallStats
            onClose={() => setShowAllTimeStats(false)}
            selectedDate={
              state.challengeResultData?.dateCompleted ||
              new Date().toISOString().split('T')[0]
            }
          />
        )}
      </Suspense>
    </div>
  );
}

export default ActiveChallenge;
